import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Spinner, Form } from "react-bootstrap";
import { FaChevronLeft, FaPlus, FaSyncAlt } from "react-icons/fa";
import { apiFetch, getUser, hasAction } from "./utils/auth";
import { humanError, errorText } from "./utils/apiError";
import { pickField } from "./utils/fieldHelpers";
// The create/edit form lives in components/ContractFormModal so the customer tab can open the
// SAME form. One copy of the signed-percent echo, deliberately — see the note in that file.
import ContractFormModal, { adjustEcho, ECHO_STYLE, LabeledInput, ERROR_BOX }
  from "./components/ContractFormModal";
// The SAME documents lane quotes and orders use — dbo.ReferenceAttachment, blob-backed, with
// its own RefDocs* entitlements and the staging->confirm upload. It was built generic on
// (EntityType, EntityId) precisely so a new anchor is a string, not a table.
import AttachmentsButton from "./components/ReferenceDocsPanel";
import GridPagination from "./components/GridPagination";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// =============================================================================
// PriceContracts — the contract-pricing admin screen at /price-contracts:
// list -> detail -> lifecycle, one page. Backed by PriceContractController.
//
// TWO FIELDS ON THIS PAGE COME FROM THE SERVER AND ARE NEVER RE-DERIVED HERE:
//
//   isLiveNow — "would this contract price a quote today?". It folds status,
//     isEnabled and the date window together against the server's clock, which
//     is the same clock the pricing engine uses. Recomputing it in the browser
//     would disagree across a timezone boundary, and for an approved-but-
//     disabled contract, and it would disagree SILENTLY — telling a rep a
//     hospital gets contract prices on a quote that will not give them.
//
//   demoted — only ever set on a PUT response, and only when that write
//     actually knocked the contract out of Active. TR_CustomerPriceContract_
//     EconomicEdit rewrites Status, IsEnabled and the approval columns
//     underneath the write, so the server measures the row before and after and
//     reports what happened. It is a fact, not a prediction: do not try to
//     guess it from which fields the form changed.
//
// Every mutation returns the FULL post-write row — render what comes back,
// never what was sent. rowVersion travels both ways; a 409 carries the current
// row (body.current) or the contract that is in the way (body.conflictingContract).
// =============================================================================

// Case-insensitive field read, same helper the rest of the SPA uses, so a
// PascalCase serializer setting would not blank the screen.
const f = (o, k, d = "") => {
  const v = pickField(o, k);
  return v === undefined || v === null ? d : v;
};

// Entitlements. NOTE the missing `, true` fallback, which every older gate in
// this repo passes: that fallback exists to keep pre-entitlement sessions from
// losing buttons they already had. A session that predates contract pricing can
// never carry 00083/00084, so defaulting them ON would paint buttons the server
// answers with 403. Default deny is the honest reading here.
const canManage = () => hasAction("00083");
const canApprove = () => hasAction("00084");

const STATUSES = ["Draft", "PendingApproval", "Active", "Expired", "Cancelled"];
const PAGE_SIZES = [10, 25, 50, 100];

const statusLabel = (s) => (String(s || "") === "PendingApproval" ? "Pending approval" : String(s || "—"));

const statusPill = (s) => {
  switch (String(s || "")) {
    case "Active": return "qw-pill-emerald";
    case "PendingApproval": return "qw-pill-amber";
    case "Cancelled": return "qw-pill-amber";
    default: return "qw-pill-slate";      // Draft, Expired, anything new
  }
};

// A DateOnly arrives as "2026-01-01". new Date() on that parses as UTC midnight
// and prints 31 Dec in US timezones — precisely the misread a contract window
// cannot afford — so build the date from its parts.
const fmtDay = (v) => {
  if (!v) return "—";
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return String(v);
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isNaN(d.getTime()) ? s : d.toLocaleDateString();
};

// Audit timestamps are real DateTimes — date AND time, local.
const fmtStamp = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};


const signedPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  const body = `${Math.abs(n)}%`;
  if (n === 0) return "0%";
  return n < 0 ? `-${body}` : `+${body}`;
};



// One mutation, one read of the body, no silent overwrites. Never throws on a
// non-2xx — apiFetch doesn't either — so the caller branches on `ok`/`status`.
// Network failures still reject; the caller catches and runs errorText.
async function mutate(url, init, fallback) {
  const r = await apiFetch(url, init);
  const body = await r.json().catch(() => ({}));
  if (r.ok) return { ok: true, status: r.status, dto: body };
  return {
    ok: false,
    status: r.status,
    message: humanError(r, body, fallback),
    current: body && body.current ? body.current : null,
    conflicting: body && body.conflictingContract ? body.conflictingContract : null,
  };
}

// ── small shared bits ────────────────────────────────────────────────────────


function Notice({ notice }) {
  if (!notice) return null;
  const err = notice.kind === "err";
  return (
    <div className="qw-info qw-info-amber" style={err ? ERROR_BOX : { marginTop: 10, background: "#ECFDF5", color: "#065F46" }}>
      <div>{notice.text}</div>
    </div>
  );
}

// LIVE NOW is the server's isLiveNow, rendered as-is. The negative case gets a
// visible chip too — a blank cell reads as "unknown", and this is the one
// question people open the screen to answer.
function LiveBadge({ live }) {
  return live === true
    ? <span className="qw-pill qw-pill-indigo" title="Pricing quotes today">● LIVE NOW</span>
    : <span className="qw-pill qw-pill-slate" title="Not pricing anything today">not pricing</span>;
}


function ReadRow({ label, children }) {
  return (
    <div className="qw-readrow">
      <label className="qw-label">{label}</label>
      <div className="qw-readval">{children}</div>
    </div>
  );
}

function AuditLine({ label, who, when, note, noteTone }) {
  return (
    <div className="qw-card-flat qw-pad" style={{ padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
        <span style={{ fontWeight: 700, color: "#0F172A", fontSize: 13 }}>{label}</span>
        <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmtStamp(when)}</span>
      </div>
      <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>by {who || "—"}</div>
      {note && (
        <div style={{ fontSize: 12, color: noteTone === "bad" ? "#B91C1C" : "#475569", marginTop: 4, whiteSpace: "pre-wrap" }}>
          {note}
        </div>
      )}
    </div>
  );
}

// ── create / edit modal ──────────────────────────────────────────────────────



function ApproveModal({ contract, saving, onClose, onSubmit }) {
  const me = getUser() || {};
  const [recordedBy, setRecordedBy] = useState(() => me.name || me.email || "");
  const [note, setNote] = useState("");
  const [err, setErr] = useState(null);

  const go = async () => {
    setErr(null);
    const res = await onSubmit({
      approvalRecordedBy: recordedBy.trim() || null,
      approvalNote: note.trim() || null,
      rowVersion: f(contract, "rowVersion", null) ?? null,
    });
    if (res && res.ok === false && res.status === 400) setErr(res.message);
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered scrollable contentClassName="qw-root">
      <Modal.Header closeButton={!saving}><Modal.Title style={{ fontSize: 18 }}>Approve {f(contract, "contractRef") || f(contract, "contractName")}</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <div className="qw-info qw-info-amber" style={{ ...ERROR_BOX, marginTop: 0, marginBottom: 12 }}><div>{err}</div></div>}
        {/* THE CHECKER'S SCREEN.
            This is the one place a second person is asked to agree the terms are right, and it
            used to show a bare "+10%" — the exact form the rest of this file argues is unreadable,
            on the only screen where misreading it is expensive. It now carries the same plain-English
            echo as the edit form, plus the window and how many hospitals it covers: the three facts
            that decide whether approving is a mistake. */}
        <p style={{ fontSize: 13, color: "#475569", marginTop: 0 }}>
          Approving makes this contract <strong>Active and enabled</strong>, and it starts pricing
          quotes for every attached hospital.
        </p>

        {(() => {
          const e = adjustEcho(f(contract, "adjustPercent", ""), f(contract, "referencePriceYear", ""));
          const tone = e.tone === "good" ? "#065F46" : e.tone === "warn" ? "#92400E" : "#3730A3";
          const bg = e.tone === "good" ? "#ECFDF5" : e.tone === "warn" ? "#FFFBEB" : "#EEF2FF";
          const count = Number(f(contract, "customerCount", 0)) || 0;
          return (
            <div style={{ background: bg, color: tone, borderRadius: 8, padding: "10px 12px",
                          fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              <div>{e.text}</div>
              <div style={{ fontWeight: 500, marginTop: 6 }}>
                Runs {fmtDay(f(contract, "contractStartDate"))} → <strong>{fmtDay(f(contract, "contractEndDate"))}</strong>
                {" · "}
                {count === 0
                  ? "no hospitals attached yet"
                  : `${count} hospital${count === 1 ? "" : "s"} attached`}
              </div>
            </div>
          );
        })()}

        {Number(f(contract, "customerCount", 0)) === 0 && (
          <div className="qw-info qw-info-amber" style={{ marginTop: 0, marginBottom: 12 }}>
            <div style={{ fontSize: 12.5 }}>
              Nothing is attached to this contract yet, so approving it changes no prices today.
              That is fine if the hospitals are being added afterwards — worth a second look if not.
            </div>
          </div>
        )}
        <div className="qw-stack">
          <LabeledInput label="Approval recorded by" hint="The president signs on paper; whoever keys it in goes here.">
            <Form.Control className="qw-input" value={recordedBy} disabled={saving} onChange={(e) => setRecordedBy(e.target.value)} />
          </LabeledInput>
          <div>
            <label className="qw-label">Approval note</label>
            <Form.Control as="textarea" rows={3} className="qw-input" value={note} disabled={saving}
              onChange={(e) => setNote(e.target.value)} placeholder="e.g. Signed copy dated 12 Sep, filed with Finance." />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-success" disabled={saving} onClick={go}>{saving ? "Approving…" : "Approve"}</button>
      </Modal.Footer>
    </Modal>
  );
}

// ── disable modal ────────────────────────────────────────────────────────────
// Disable only. Enable does NOT ask for a reason: there is no column for one,
// enable clears disabledBy/disabledAt/disableReason, and a required box whose
// contents go nowhere is a lie to whoever fills it in.

function DisableModal({ contract, saving, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [err, setErr] = useState(null);

  const go = async () => {
    if (!reason.trim()) { setErr("Say why it is being switched off — the reason is kept on the contract."); return; }
    setErr(null);
    const res = await onSubmit({ reason: reason.trim(), rowVersion: f(contract, "rowVersion", null) ?? null });
    if (res && res.ok === false && res.status === 400) setErr(res.message);
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered scrollable contentClassName="qw-root">
      <Modal.Header closeButton={!saving}><Modal.Title style={{ fontSize: 18 }}>Disable {f(contract, "contractRef") || f(contract, "contractName")}</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <div className="qw-info qw-info-amber" style={{ ...ERROR_BOX, marginTop: 0, marginBottom: 12 }}><div>{err}</div></div>}
        <p style={{ fontSize: 13, color: "#475569", marginTop: 0 }}>
          While it is disabled, quotes for the {f(contract, "customerCount", 0)} attached customer(s) go back to
          catalogue pricing straight away.
        </p>
        <label className="qw-label">Reason</label>
        <Form.Control as="textarea" rows={3} className="qw-input" value={reason} disabled={saving}
          onChange={(e) => setReason(e.target.value)} placeholder="e.g. Hospital disputes the schedule — paused pending review." />
        <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 6 }}>
          This is recorded on the contract as the reason it was switched off, with your name and the time.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-danger" disabled={saving} onClick={go}>{saving ? "Disabling…" : "Disable contract"}</button>
      </Modal.Footer>
    </Modal>
  );
}

// ── attach-customer modal ────────────────────────────────────────────────────

function AttachCustomerModal({ saving, onClose, onSubmit }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);   // null = not searched yet
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [manual, setManual] = useState("");
  const [err, setErr] = useState(null);

  const search = async () => {
    const term = q.trim();
    if (!term) return;
    setSearching(true); setErr(null); setPicked(null);
    try {
      // Two passes merged: the term may be a customer ID (3PROSP772) or part of
      // a name, and one box should find either.
      const one = async (key) => {
        const p = new URLSearchParams({ top: "10", skip: "0" });
        p.set(key, term);
        const r = await apiFetch(`${API_URL}/api/Customer?${p.toString()}`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(humanError(r, d, "Couldn't search customers."));
        const list = d.customers || d.Customers || (Array.isArray(d) ? d : []);
        return Array.isArray(list) ? list : [];
      };
      const [byId, byName] = await Promise.all([one("idSearch"), one("nameSearch")]);
      const seen = new Set();
      const merged = [];
      [...byId, ...byName].forEach((c) => {
        const n = String(f(c, "custNum", ""));
        if (!n || seen.has(n)) return;
        seen.add(n);
        merged.push(c);
      });
      setResults(merged.slice(0, 12));
    } catch (e) {
      setErr(errorText(e, "Couldn't search customers."));
      setResults([]);
    } finally { setSearching(false); }
  };

  const go = async () => {
    const chosen = picked
      ? { custNum: Number(f(picked, "custNum")), custId: f(picked, "custID") || null, customerName: f(picked, "name") || null }
      : (/^\d+$/.test(manual.trim()) ? { custNum: Number(manual.trim()), custId: null, customerName: null } : null);
    if (!chosen) { setErr("Pick a customer from the search, or type their customer number."); return; }
    setErr(null);
    const res = await onSubmit(chosen);
    if (res && res.ok === false && res.status === 400) setErr(res.message);
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered scrollable contentClassName="qw-root">
      <Modal.Header closeButton={!saving}><Modal.Title style={{ fontSize: 18 }}>Attach a customer</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <div className="qw-info qw-info-amber" style={{ ...ERROR_BOX, marginTop: 0, marginBottom: 12 }}><div>{err}</div></div>}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Form.Control className="qw-input" value={q} disabled={saving || searching} style={{ flex: "1 1 200px", minWidth: 0 }}
            placeholder="Customer ID or name…" onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }} />
          <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={saving || searching || !q.trim()} onClick={search}>
            {searching ? <><Spinner animation="border" size="sm" /> Searching…</> : "Search"}
          </button>
        </div>

        {results !== null && results.length === 0 && !searching && (
          <div style={{ color: "#94A3B8", fontSize: 13, marginTop: 12 }}>No customers match that.</div>
        )}

        {results !== null && results.length > 0 && (
          <div className="qw-stack" style={{ marginTop: 12, maxHeight: 260, overflowY: "auto" }}>
            {results.map((c) => {
              const on = picked && String(f(picked, "custNum")) === String(f(c, "custNum"));
              return (
                <button key={String(f(c, "custNum"))} type="button" className="qw-card-flat qw-pad"
                  onClick={() => setPicked(c)} disabled={saving}
                  style={{ padding: "10px 12px", textAlign: "left", width: "100%", cursor: "pointer",
                    borderColor: on ? "#4F46E5" : "#E2E8F0", background: on ? "#EEF2FF" : "#fff" }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
                    <span className="qw-mono" style={{ fontWeight: 700, color: "#4F46E5" }}>{f(c, "custID")}</span>
                    <span style={{ fontWeight: 600, color: "#0F172A" }}>{f(c, "name")}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "#94A3B8" }}>#{f(c, "custNum")}</div>
                </button>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 16, borderTop: "1px solid #E2E8F0", paddingTop: 12 }}>
          <LabeledInput label="Or type the customer number" hint="The ID and name are filled in from Epicor when they are left out.">
            <Form.Control className="qw-input" inputMode="numeric" value={manual} disabled={saving}
              onChange={(e) => { setManual(e.target.value); setPicked(null); }} style={{ maxWidth: 200 }} />
          </LabeledInput>
        </div>

        <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 12 }}>
          Attaching someone who is already on this contract changes nothing, and re-attaching a customer who was
          detached brings their row back.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-primary" disabled={saving} onClick={go}>{saving ? "Attaching…" : "Attach"}</button>
      </Modal.Footer>
    </Modal>
  );
}

// ── conflict banner (409) ────────────────────────────────────────────────────

function ConflictBanner({ conflict, onReloadCurrent, onReloadServer, onOpenOther }) {
  if (!conflict) return null;
  const other = conflict.conflicting;
  return (
    <div className="qw-info qw-info-amber" style={{ marginTop: 12, alignItems: "flex-start" }}>
      <div style={{ width: "100%" }}>
        <strong>{other ? "Already on another contract" : "Someone else changed this"}</strong>
        <div style={{ marginTop: 4 }}>{conflict.message}</div>

        {other && (
          <div className="qw-card-flat qw-pad" style={{ padding: "10px 12px", marginTop: 10, background: "#fff" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline" }}>
              <span className="qw-mono" style={{ fontWeight: 700, color: "#4F46E5" }}>{f(other, "contractRef") || `contract ${f(other, "id")}`}</span>
              <span style={{ fontWeight: 600, color: "#0F172A" }}>{f(other, "contractName")}</span>
              <span className={`qw-pill ${statusPill(f(other, "status"))}`}>{statusLabel(f(other, "status"))}</span>
              <LiveBadge live={f(other, "isLiveNow", false) === true} />
            </div>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
              Runs {fmtDay(f(other, "contractStartDate"))} → <strong>{fmtDay(f(other, "contractEndDate"))}</strong>
              {f(other, "effectDescription") ? ` · ${f(other, "effectDescription")}` : ""}
            </div>
            {onOpenOther && (
              <button className="qw-btn qw-btn-outline qw-btn-sm" style={{ marginTop: 8 }}
                onClick={() => onOpenOther(f(other, "id"))}>Open that contract</button>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {conflict.current
            ? <button className="qw-btn qw-btn-primary qw-btn-sm" onClick={onReloadCurrent}>Reload the current version</button>
            : <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={onReloadServer}><FaSyncAlt style={{ marginRight: 6 }} /> Reload</button>}
        </div>
      </div>
    </div>
  );
}

// ── detail ───────────────────────────────────────────────────────────────────

function ContractDetail({ id, onBack, onChanged, onOpenOther }) {
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [demoted, setDemoted] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  const manage = canManage();
  const approve = canApprove();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/price-contracts/${id}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(humanError(r, d, "That contract couldn't be found — it may have been removed."));
      setContract(d);
      setConflict(null);
      setDemoted(false);
    } catch (e) { setError(errorText(e, "Couldn't load this contract.")); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // One funnel for every mutation: render the returned row, surface a 409 with
  // whatever the server sent with it, surface everything else as the server's
  // own sentence.
  const run = useCallback(async (fn, okMsg, fallback) => {
    setBusy(true); setNotice(null); setConflict(null); setDemoted(false);
    try {
      const res = await fn();
      if (res.ok) {
        setContract(res.dto);
        if (f(res.dto, "demoted", null) === true) setDemoted(true);
        else setNotice({ kind: "ok", text: okMsg });
        if (onChanged) onChanged();
        return res;
      }
      if (res.status === 409) { setConflict({ message: res.message, current: res.current, conflicting: res.conflicting }); return res; }
      // 403 lands here too — for approve that is maker-checker firing, and the
      // server's own sentence says which half of it (created / submitted).
      setNotice({ kind: "err", text: res.message });
      return res;
    } catch (e) {
      setNotice({ kind: "err", text: errorText(e, fallback) });
      return { ok: false, status: 0 };
    } finally { setBusy(false); }
  }, [onChanged]);

  const saveEdit = async (payload) => {
    const res = await run(
      () => mutate(`${API_URL}/api/price-contracts/${id}`, { method: "PUT", body: JSON.stringify(payload) }, "Couldn't save the contract."),
      "Saved.", "Couldn't save the contract.");
    if (!(res.ok === false && res.status === 400)) setEditOpen(false);
    return res;
  };

  const doApprove = async (payload) => {
    const res = await run(
      () => mutate(`${API_URL}/api/price-contracts/${id}/approve`, { method: "POST", body: JSON.stringify(payload) }, "Couldn't approve this contract."),
      "Approved — this contract is Active and enabled.", "Couldn't approve this contract.");
    if (!(res.ok === false && res.status === 400)) setApproveOpen(false);
    return res;
  };

  const doDisable = async (payload) => {
    const res = await run(
      () => mutate(`${API_URL}/api/price-contracts/${id}/disable`, { method: "POST", body: JSON.stringify(payload) }, "Couldn't disable this contract."),
      "Disabled. It is not pricing anything now.", "Couldn't disable this contract.");
    if (!(res.ok === false && res.status === 400)) setDisableOpen(false);
    return res;
  };

  const doAttach = async (payload) => {
    const res = await run(
      () => mutate(`${API_URL}/api/price-contracts/${id}/customers`, { method: "POST", body: JSON.stringify(payload) }, "Couldn't attach that customer."),
      "Customer attached.", "Couldn't attach that customer.");
    if (!(res.ok === false && res.status === 400)) setAttachOpen(false);
    return res;
  };

  const doSubmit = () => run(
    () => mutate(`${API_URL}/api/price-contracts/${id}/submit`, { method: "POST", body: JSON.stringify({}) }, "Couldn't submit this contract."),
    "Submitted for approval.", "Couldn't submit this contract.");

  const doEnable = () => {
    if (!window.confirm("Switch this contract back on? Quotes for its attached customers will be priced from the contract again straight away.")) return;
    // No reason box: enable does not persist one, and it clears the disable record.
    run(() => mutate(`${API_URL}/api/price-contracts/${id}/enable`,
      { method: "POST", body: JSON.stringify({ rowVersion: f(contract, "rowVersion", null) ?? null }) },
      "Couldn't enable this contract."), "Enabled.", "Couldn't enable this contract.");
  };

  const doDetach = (row) => {
    const who = f(row, "custId") || `#${f(row, "custNum")}`;
    const name = f(row, "customerName") ? ` (${f(row, "customerName")})` : "";
    if (!window.confirm(`Detach ${who}${name} from this contract? Their next quote is priced from the catalogue instead. The row stays visible with who removed it and when.`)) return;
    run(() => mutate(`${API_URL}/api/price-contracts/${id}/customers/${f(row, "custNum")}`, { method: "DELETE" },
      "Couldn't detach that customer."), `${who} detached.`, "Couldn't detach that customer.");
  };

  if (loading) {
    return <div className="qw-card qw-pad" style={{ textAlign: "center", color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading contract…</div>;
  }
  if (error) {
    return (
      <div className="qw-card qw-pad">
        <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={onBack}><FaChevronLeft /> Back to contracts</button>
        <div className="qw-info qw-info-amber" style={ERROR_BOX}><div>{error}</div></div>
      </div>
    );
  }
  if (!contract) return null;

  const status = String(f(contract, "status"));
  const live = f(contract, "isLiveNow", false) === true;
  const enabled = f(contract, "isEnabled", false) === true;
  const customers = Array.isArray(f(contract, "customers", [])) ? f(contract, "customers", []) : [];
  const activeRows = customers.filter((c) => f(c, "isActive", false) === true);
  const detachedRows = customers.filter((c) => f(c, "isActive", false) !== true);
  const echo = adjustEcho(f(contract, "adjustPercent", ""), f(contract, "referencePriceYear", ""));

  // Courtesy only. The SPA has no user id — the server compares the integer
  // UserId claim, the DTO carries only display strings — so this can be wrong
  // either way. It never hides the button: the 403 is the actual control and
  // gets rendered when it fires.
  const me = getUser() || {};
  const myMark = String(me.email || "").toLowerCase();
  const likelyMine = !!myMark && (
    String(f(contract, "createdBy")).toLowerCase() === myMark ||
    String(f(contract, "submittedBy")).toLowerCase() === myMark);

  return (
    <div className="qw-fade">
      <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={onBack}><FaChevronLeft /> Back to contracts</button>

      <div className="qw-card qw-pad" style={{ marginTop: 10 }}>
        <div className="qw-eyebrow">{f(contract, "contractRef") || `Contract ${f(contract, "id")}`}</div>
        <h2 className="qw-h2" style={{ marginTop: 2 }}>{f(contract, "contractName")}</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
          <span className={`qw-pill ${statusPill(status)}`}>{statusLabel(status)}</span>
          {/* isLiveNow is the server's answer, not a browser calculation. */}
          <LiveBadge live={live} />
          <span className="qw-pill qw-pill-slate">{enabled ? "enabled" : "switched off"}</span>
          <span className="qw-pill qw-pill-violet">{f(contract, "customerCount", 0)} customer(s)</span>
        </div>
        <p className="qw-sub" style={{ marginTop: 8 }}>
          {f(contract, "effectDescription") || "—"} · {fmtDay(f(contract, "contractStartDate"))} → {fmtDay(f(contract, "contractEndDate"))}
        </p>

        {!live && (
          <div style={{ fontSize: 12.5, color: "#64748B", marginTop: 6 }}>
            Nothing is priced from this contract today.
          </div>
        )}

        {/* ── lifecycle ── */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
          {manage && (
            <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={busy} onClick={() => setEditOpen(true)}>Edit</button>
          )}
          {/* Draft only. The server also tolerates re-submitting something already
              PendingApproval; offering that button would only invite someone to
              re-stamp submittedBy on a contract that is already in the queue. */}
          {manage && status === "Draft" && (
            <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={busy} onClick={doSubmit}>Submit for approval</button>
          )}
          {approve && status === "PendingApproval" && (
            <button className="qw-btn qw-btn-success qw-btn-sm" disabled={busy} onClick={() => setApproveOpen(true)}>Approve</button>
          )}
          {approve && (enabled
            ? <button className="qw-btn qw-btn-danger qw-btn-sm" disabled={busy} onClick={() => setDisableOpen(true)}>Disable</button>
            : <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={busy || status !== "Active"} onClick={doEnable}
                title={status === "Active" ? "" : "Only an approved (Active) contract can be switched on."}>Enable</button>)}
          {busy && <span style={{ color: "#64748B", fontSize: 13, alignSelf: "center" }}><Spinner animation="border" size="sm" /> Working…</span>}
        </div>

        {approve && status === "PendingApproval" && likelyMine && (
          <div style={{ fontSize: 12, color: "#B45309", marginTop: 8 }}>
            You look like the person who raised this one. Approval needs a second pair of hands — the server will
            refuse it if it was you.
          </div>
        )}

        {!manage && !approve && (
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 10 }}>
            You can view contracts but not change them.
          </div>
        )}

        {/* demoted is a fact reported by the PUT response, never predicted here. */}
        {demoted && (
          <div className="qw-info qw-info-amber" style={{ marginTop: 12 }}>
            <div>
              <strong>Saved — and this contract is no longer Active.</strong>
              <div style={{ marginTop: 4 }}>
                Changing the discount, the price year or either date takes a contract out of Active and clears its
                approval. It needs approving again before it prices anything, and nothing is being priced from it
                in the meantime.
              </div>
            </div>
          </div>
        )}

        <Notice notice={notice} />
        <ConflictBanner
          conflict={conflict}
          onReloadCurrent={() => { setContract(conflict.current); setConflict(null); setNotice({ kind: "ok", text: "Showing the current version — your change was not saved." }); }}
          onReloadServer={load}
          onOpenOther={onOpenOther}
        />
      </div>

      {/* ── terms ── */}
      <div className="qw-card qw-pad" style={{ marginTop: 14 }}>
        <div className="qw-eyebrow">Terms</div>
        <div className="qw-grid-auto" style={{ marginTop: 10 }}>
          <ReadRow label="Reference price year">{f(contract, "referencePriceYear", "—")}</ReadRow>
          <ReadRow label="Adjustment">{signedPct(f(contract, "adjustPercent"))}</ReadRow>
          <ReadRow label="Starts">{fmtDay(f(contract, "contractStartDate"))}</ReadRow>
          <ReadRow label="Ends">{fmtDay(f(contract, "contractEndDate"))}</ReadRow>
          <ReadRow label="Signing entity">{f(contract, "signingEntityName") || "—"}</ReadRow>
          <ReadRow label="Signing customer #">{f(contract, "signingCustNum") === "" ? "—" : f(contract, "signingCustNum")}</ReadRow>
          <ReadRow label="Signed copy">{f(contract, "attachmentId") === "" ? "not linked" : `attachment ${f(contract, "attachmentId")}`}</ReadRow>
          <ReadRow label="Contract ref"><span className="qw-mono">{f(contract, "contractRef") || "—"}</span></ReadRow>
        </div>
        <div style={{ ...ECHO_STYLE[echo.tone], borderRadius: 12, padding: "12px 14px", marginTop: 14, fontSize: 14, fontWeight: 700 }}>
          {echo.text}
        </div>
        <div style={{ marginTop: 14 }}>
          <div className="qw-eyebrow">Notes</div>
          <div style={{ fontSize: 13, color: f(contract, "notes") ? "#0F172A" : "#94A3B8", marginTop: 4, whiteSpace: "pre-wrap" }}>
            {f(contract, "notes") || "No notes on this contract."}
          </div>
        </div>

        {/* ── The signed paperwork ──────────────────────────────────────────────────
            The same lane quotes and orders use, anchored to this contract. It matters more
            here than almost anywhere else: the price a hospital pays is decided by a document
            somebody signed, and when that price is disputed eighteen months from now the
            argument is settled by producing it. A contract whose signed copy lives in
            somebody's email is a contract nobody can defend.

            Entitlements compose — the documents lane enforces its own RefDocs* actions, and
            RecordAccessService additionally requires 00082 to touch a Contract anchor. */}
        <div style={{ marginTop: 18, borderTop: "1px solid #E2E8F0", paddingTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <div>
              <div className="qw-eyebrow">Documents</div>
              <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>
                The signed contract, amendments, the price workbook — whatever this contract
                would have to be proven with.
              </p>
            </div>
            <AttachmentsButton
              entityType="Contract"
              entityId={f(contract, "id")}
              style={{ background: "#4F46E5", color: "#fff", fontWeight: 700 }}
            />
          </div>
        </div>
      </div>

      {/* ── audit ── */}
      <div className="qw-card qw-pad" style={{ marginTop: 14 }}>
        <div className="qw-eyebrow">Audit</div>
        <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>Who moved this contract, and when.</p>
        <div className="qw-stack" style={{ marginTop: 10 }}>
          <AuditLine label="Created" who={f(contract, "createdBy")} when={f(contract, "createdAt")} />
          <AuditLine label="Submitted for approval" who={f(contract, "submittedBy")} when={f(contract, "submittedAt")} />
          <AuditLine
            label="Approved"
            who={f(contract, "approvedBy")}
            when={f(contract, "approvedAt")}
            note={[
              f(contract, "approvalRecordedBy") ? `Recorded by ${f(contract, "approvalRecordedBy")}` : "",
              f(contract, "approvalNote"),
            ].filter(Boolean).join(" · ")}
          />
          <AuditLine
            label="Disabled"
            who={f(contract, "disabledBy")}
            when={f(contract, "disabledAt")}
            note={f(contract, "disableReason")}
            noteTone="bad"
          />
        </div>
        <div className="qw-mono" style={{ fontSize: 10.5, color: "#CBD5E1", marginTop: 10, wordBreak: "break-all" }}>
          id {f(contract, "id")} · rowVersion {f(contract, "rowVersion") || "—"}
        </div>
      </div>

      {/* ── customers ── */}
      <div className="qw-card qw-pad" style={{ marginTop: 14, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <div className="qw-eyebrow">Customers on this contract</div>
            <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>
              {activeRows.length} attached{detachedRows.length ? ` · ${detachedRows.length} detached, kept for the record` : ""}
            </p>
          </div>
          {manage && (
            <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={busy} onClick={() => setAttachOpen(true)}>
              <FaPlus style={{ marginRight: 6 }} /> Attach customer
            </button>
          )}
        </div>

        {customers.length === 0 && (
          <div style={{ color: "#94A3B8", fontSize: 13, marginTop: 12 }}>
            Nobody is on this contract yet, so it prices nothing. Attach the hospitals it covers.
          </div>
        )}

        <div className="qw-stack" style={{ marginTop: 12 }}>
          {activeRows.concat(detachedRows).map((row, i) => {
            const off = f(row, "isActive", false) !== true;
            return (
              <div key={f(row, "id") || `${f(row, "custNum")}-${i}`} className="qw-card-flat qw-pad"
                style={{ padding: "12px 14px", background: off ? "#F8FAFC" : "#fff", opacity: off ? 0.8 : 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "baseline", minWidth: 0 }}>
                    <span className="qw-mono" style={{ fontWeight: 700, color: off ? "#94A3B8" : "#4F46E5", textDecoration: off ? "line-through" : "none" }}>
                      {f(row, "custId") || `#${f(row, "custNum")}`}
                    </span>
                    <span style={{ fontWeight: 600, color: off ? "#94A3B8" : "#0F172A", textDecoration: off ? "line-through" : "none", wordBreak: "break-word" }}>
                      {f(row, "customerName") || "—"}
                    </span>
                    {off && <span className="qw-pill qw-pill-slate">detached</span>}
                  </div>
                  {manage && !off && (
                    <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy} onClick={() => doDetach(row)}>Detach</button>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 4 }}>
                  added by {f(row, "addedBy") || "—"} · {fmtStamp(f(row, "addedAt"))}
                </div>
                {off && (
                  <div style={{ fontSize: 11.5, color: "#B45309", marginTop: 2 }}>
                    removed by {f(row, "removedBy") || "—"} · {fmtStamp(f(row, "removedAt"))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {editOpen && <ContractFormModal initial={contract} saving={busy} onClose={() => setEditOpen(false)} onSubmit={saveEdit} />}
      {approveOpen && <ApproveModal contract={contract} saving={busy} onClose={() => setApproveOpen(false)} onSubmit={doApprove} />}
      {disableOpen && <DisableModal contract={contract} saving={busy} onClose={() => setDisableOpen(false)} onSubmit={doDisable} />}
      {attachOpen && <AttachCustomerModal saving={busy} onClose={() => setAttachOpen(false)} onSubmit={doAttach} />}
    </div>
  );
}

// ── list ─────────────────────────────────────────────────────────────────────

const TH = { padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" };
const TD = { padding: "10px 12px", verticalAlign: "top" };

function ContractList({ onOpen, reloadKey }) {
  const manage = canManage();
  const [status, setStatus] = useState("");
  const [year, setYear] = useState("");
  const [text, setText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [items, setItems] = useState(null);      // null = never loaded, [] = loaded and empty
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState(null);

  // Only a complete four-digit year goes to the server: `short?` model binding
  // rejects junk with a 400 that says nothing useful, and holding the partial
  // value out of the dependency list keeps "2026" from firing four requests.
  const yearParam = /^\d{4}$/.test(String(year).trim()) ? String(year).trim() : "";

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (status) params.set("status", status);
      if (yearParam) params.set("year", yearParam);
      const r = await apiFetch(`${API_URL}/api/price-contracts?${params.toString()}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(humanError(r, d, "Couldn't load the contracts list."));
      const list = d.items || d.Items || [];
      setItems(Array.isArray(list) ? list : []);
      const n = d.totalCount !== undefined ? d.totalCount : d.TotalCount;
      setTotalCount(typeof n === "number" ? n : 0);
    } catch (e) {
      setError(errorText(e, "Couldn't load the contracts list."));
      setItems([]); setTotalCount(0);
    } finally { setLoading(false); }
  }, [status, yearParam, page, pageSize]);

  useEffect(() => { load(); }, [load, reloadKey]);

  // The API filters on status / year / custNum only. This box narrows the rows
  // already on screen — it is not a server search, and the empty state says so
  // rather than implying the contract does not exist.
  const shown = useMemo(() => {
    const rows = items || [];
    const q = text.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) => `${f(c, "contractRef")} ${f(c, "contractName")} ${f(c, "signingEntityName")}`.toLowerCase().includes(q));
  }, [items, text]);

  const create = async (payload) => {
    setCreating(true); setNotice(null);
    try {
      const res = await mutate(`${API_URL}/api/price-contracts`, { method: "POST", body: JSON.stringify(payload) }, "Couldn't create the contract.");
      if (res.ok) {
        setCreateOpen(false);
        onOpen(f(res.dto, "id"));      // straight into the new row's detail
        return res;
      }
      if (res.status !== 400) { setCreateOpen(false); setNotice({ kind: "err", text: res.message }); }
      return res;
    } catch (e) {
      setNotice({ kind: "err", text: errorText(e, "Couldn't create the contract.") });
      return { ok: false, status: 0 };
    } finally { setCreating(false); }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const rowsOnPage = (items || []).length;

  const resetPage = (fn) => { setPage(1); fn(); };

  return (
    <div className="qw-fade">
      <div className="qw-card qw-pad">
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <div className="qw-eyebrow">Contract pricing</div>
            <h2 className="qw-h2" style={{ marginTop: 2 }}>Price contracts</h2>
            <p className="qw-sub">
              Hospitals that pay a past year's book price, plus or minus a percentage, instead of the live catalogue.
            </p>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={loading} onClick={load}>
              <FaSyncAlt style={{ marginRight: 6 }} /> Refresh
            </button>
            {manage && (
              <button className="qw-btn qw-btn-primary qw-btn-sm" onClick={() => setCreateOpen(true)}>
                <FaPlus style={{ marginRight: 6 }} /> New contract
              </button>
            )}
          </div>
        </div>

        <div className="qw-grid-3" style={{ marginTop: 16 }}>
          <LabeledInput label="Status">
            <Form.Select className="qw-input" value={status} disabled={loading}
              onChange={(e) => { const v = e.target.value; resetPage(() => setStatus(v)); }}>
              <option value="">Any status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
            </Form.Select>
          </LabeledInput>
          <LabeledInput label="Reference price year">
            <Form.Control className="qw-input" inputMode="numeric" placeholder="e.g. 2026" value={year} disabled={loading}
              onChange={(e) => { const v = e.target.value; resetPage(() => setYear(v)); }} />
          </LabeledInput>
          <LabeledInput label="Find on this page" hint="Narrows the rows below by name or reference.">
            <Form.Control className="qw-input" placeholder="Name or reference…" value={text}
              onChange={(e) => setText(e.target.value)} />
          </LabeledInput>
        </div>

        <Notice notice={notice} />
        {error && <div className="qw-info qw-info-amber" style={ERROR_BOX}><div>{error}</div></div>}
      </div>

      <div className="qw-card" style={{ marginTop: 14, overflow: "hidden" }}>
        {loading && (
          <div style={{ padding: 24, textAlign: "center", color: "#64748B" }}>
            <Spinner animation="border" size="sm" /> Loading contracts…
          </div>
        )}

        {!loading && !error && rowsOnPage === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
            {status || year
              ? "No contracts match these filters."
              : "No price contracts yet. Every customer is priced from the live catalogue."}
          </div>
        )}

        {!loading && !error && rowsOnPage > 0 && shown.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "#94A3B8", fontSize: 14 }}>
            Nothing on this page matches “{text.trim()}”. It only looks at the {rowsOnPage} row(s) shown — try another
            page, or filter by status or year.
          </div>
        )}

        {!loading && shown.length > 0 && (
          <>
            {/* Desktop grid. The phone build below is a separate render of the
                same rows — that is how this repo keeps a wide table from
                scrolling sideways at 375px. */}
            <div className="d-none d-lg-block">
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                <colgroup>
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "27%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "17%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "9%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                    <th style={TH}>Reference</th>
                    <th style={TH}>Contract</th>
                    <th style={TH}>Year</th>
                    <th style={TH}>Adjust</th>
                    <th style={TH}>Window</th>
                    <th style={TH}>Status</th>
                    <th style={TH}>Customers</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((c, i) => (
                    <tr key={f(c, "id") || i} style={{ borderTop: "1px solid #F1F5F9", cursor: "pointer" }}
                      onClick={() => onOpen(f(c, "id"))} title="Open this contract">
                      <td style={TD}><span className="qw-mono" style={{ fontWeight: 600, color: "#4F46E5" }}>{f(c, "contractRef") || `#${f(c, "id")}`}</span></td>
                      <td style={TD}>
                        <div style={{ fontWeight: 600, color: "#0F172A", wordBreak: "break-word" }}>{f(c, "contractName")}</div>
                        {/* effectDescription is the server's plain-English line — not rebuilt here. */}
                        <div style={{ fontSize: 11.5, color: "#64748B" }}>{f(c, "effectDescription") || "—"}</div>
                      </td>
                      <td style={TD}>{f(c, "referencePriceYear", "—")}</td>
                      <td style={{ ...TD, fontWeight: 700, color: Number(f(c, "adjustPercent", 0)) < 0 ? "#047857" : Number(f(c, "adjustPercent", 0)) > 0 ? "#B45309" : "#475569" }}>
                        {signedPct(f(c, "adjustPercent"))}
                      </td>
                      <td style={{ ...TD, color: "#475569" }}>
                        {fmtDay(f(c, "contractStartDate"))}<br />→ {fmtDay(f(c, "contractEndDate"))}
                      </td>
                      <td style={TD}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <span className={`qw-pill ${statusPill(f(c, "status"))}`}>{statusLabel(f(c, "status"))}</span>
                          <LiveBadge live={f(c, "isLiveNow", false) === true} />
                        </div>
                      </td>
                      <td style={TD}>{f(c, "customerCount", 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Phone */}
            <div className="d-lg-none" style={{ padding: 12 }}>
              <div className="qw-stack">
                {shown.map((c, i) => (
                  <button key={f(c, "id") || i} type="button" className="qw-card-flat qw-pad"
                    onClick={() => onOpen(f(c, "id"))}
                    style={{ padding: "12px 14px", textAlign: "left", width: "100%", cursor: "pointer", background: "#fff" }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span className="qw-mono" style={{ fontWeight: 700, color: "#4F46E5" }}>{f(c, "contractRef") || `#${f(c, "id")}`}</span>
                      <span className={`qw-pill ${statusPill(f(c, "status"))}`}>{statusLabel(f(c, "status"))}</span>
                      <LiveBadge live={f(c, "isLiveNow", false) === true} />
                    </div>
                    <div style={{ fontWeight: 600, color: "#0F172A", marginTop: 6, wordBreak: "break-word" }}>{f(c, "contractName")}</div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>{f(c, "effectDescription") || "—"}</div>
                    <div style={{ fontSize: 12, color: "#475569", marginTop: 6 }}>
                      {fmtDay(f(c, "contractStartDate"))} → {fmtDay(f(c, "contractEndDate"))}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>
                      {signedPct(f(c, "adjustPercent"))} on the {f(c, "referencePriceYear")} book · {f(c, "customerCount", 0)} customer(s)
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {!loading && !error && rowsOnPage > 0 && (
        <GridPagination
          currentPage={page - 1}
          totalPages={totalPages}
          totalCount={totalCount}
          showFrom={(page - 1) * pageSize + 1}
          showTo={(page - 1) * pageSize + rowsOnPage}
          onGoToPage={(zeroBased) => setPage(zeroBased + 1)}
          extra={
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, color: "#64748B" }}>Rows</span>
              <Form.Select size="sm" value={pageSize} aria-label="Page size" style={{ width: "auto", minWidth: 64 }}
                onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </div>
          }
        />
      )}

      {createOpen && <ContractFormModal initial={null} saving={creating} onClose={() => setCreateOpen(false)} onSubmit={create} />}
    </div>
  );
}

// ── page ─────────────────────────────────────────────────────────────────────

export default function PriceContracts() {
  const [openId, setOpenId] = useState(null);
  const [listVersion, setListVersion] = useState(0);

  const open = (id) => {
    if (id === undefined || id === null || id === "") return;
    setOpenId(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1180px, 96vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      {/* The list stays mounted while a contract is open so the filters and the
          page someone was on are still there when they come back. */}
      <div style={{ display: openId === null ? "block" : "none" }}>
        <ContractList onOpen={open} reloadKey={listVersion} />
      </div>
      {openId !== null && (
        <ContractDetail
          key={openId}
          id={openId}
          onBack={() => { setOpenId(null); setListVersion((n) => n + 1); }}
          onChanged={() => setListVersion((n) => n + 1)}
          onOpenOther={open}
        />
      )}
    </div>
  );
}
